import {
  popupStore,
  freeBetStore,
  userInfoStore,
  roundInfoStore,
  gameConfigStore,
  applicationStore,
  notificationStore,
  loadingStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  GameModes,
  parseQuery,
  BetResponseCode,
  DefaultQueryParams,
  tableBetHistoryStore,
} from '@pascal/pascal-core';
import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';

import {
  CRAZY_WORM_GAME_TYPE_ID,
  INFINITY_AUTO_BET,
  maxOddValue,
  MIN_ODD,
} from '../enums/constants';
import { liveConnection } from '../core';
import { gameStore } from '../store/gameStore';
import { doBetStore } from '../store/doBetStore';
import { autoBetStore } from '../store/autoBetStore';
import { winningStore } from '../store/winningStore';
import { gameLiveConnection } from './GameConnection';
import { soundService, soundTypes } from './SoundService';
import { liveBetsDataStore } from '../store/liveBetsDataStore';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { webGameSceneDrawer } from '../game/drawers/gameDrawer/gameSceneDrawer';

let timeOutId;

class GameService {
  onGetInitialStateResult({
    rate,
    limits,
    balance,
    precision,
    liveBets,
    currencyCode,
    isTestPlatform,
    gameConfiguration,
  }) {
    this.handleResetDataInitial();
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
      [DefaultQueryParams.Mode]: mode,
    } = parseQuery(window.location.search);
    userInfoStore.setUserInfo({
      rate,
      userId,
      balance,
      precision,
      currencyCode,
      currencySymbol,
    });

    liveBetsDataStore.setLiveBets(liveBets);
    gameConfigStore.setLimits(limits);
    doBetStore.setSelectedOdd(MIN_ODD);
    applicationStore.setIsDemo(mode === GameModes.Fun || isTestPlatform);
    applicationStore.setIsTestPlatform(isTestPlatform);
    doBetStore.setQuickBets(limits?.quickBets);
    doBetStore.setSelectedAmount(
      limits?.defaultBetAmount || limits?.quickBets[1] > balance
        ? limits?.quickBets[0]
        : limits?.quickBets[1]
    );
    gameStore.setGameTypeId();
    loadingStore.updateLoading({ isGameInitialLoaded: true });

    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();

    gameConfigStore.setGameConfiguration(gameConfiguration);
    const { turboBet, resultDelay: doBetDelay } = gameConfiguration || {};

    if (doBetDelay) {
      gameStore.setDoBetDelay(doBetDelay);
    }

    if (turboBet) {
      gameStore.setIsTurboModeExist(turboBet);
    } else if (!userSettingsStore.isAnimationOn) {
      userSettingsStore.setIsAnimationOn();
    }
  }

  handleDoBetResult({ finishedRound, betResult }) {
    doBetStore.setIsBetAccepted(false);
    const { isAnimationOn } = userSettingsStore;
    if (isAnimationOn) {
      webGameSceneDrawer.crazyWormShoot();
    }

    roundInfoStore.setRoundId(betResult.roundId);

    if (this.timeOutId1) {
      clearTimeout(this.timeOutId1);
    }

    if (this.timeOutId2) {
      clearTimeout(this.timeOutId2);
    }

    if (this.timeOutId3) {
      clearTimeout(this.timeOutId3);
    }

    this.timeOutId1 = setTimeout(
      () => {
        doBetStore.setRoundFinResult(+finishedRound.roundData);
      },
      isAnimationOn ? 500 : 0
    );
    this.timeOutId2 = setTimeout(
      () => {
        doBetStore.setRoundIsFin(true);
      },
      isAnimationOn ? 900 : 0
    );
    this.timeOutId3 = setTimeout(
      () => {
        gameStore.updatedPastRoundsData({
          userId: betResult.userId,
          winAmount: betResult.winAmount,
          ...finishedRound,
        });
      },
      isAnimationOn ? 800 : 0
    );

    this.handleRoundWinning(betResult, betResult.freeBetId);
    freeBetStore.handleFreeBetDoBetResult(betResult?.freeBetId);
  }

  handleDoBetResultValidation({ betResult: { responseCode } }) {
    notificationStore.setNotificationType(responseCode);
    doBetStore.setIsBetAccepted(false);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
    if (autoBetStore.isPlay) {
      autoBetStore.resetAutoBet();
    }
    gameStore.setIsBetsClosed(false);
  }

  sendBet() {
    if (!doBetStore.isBetAccepted) {
      doBetStore.setRoundIsFin(false);
      doBetStore.setIsBetAccepted(true);
      const { activeFreeBet } = freeBetStore;

      if (!doBetStore.selectedOdd || doBetStore.selectedOdd < MIN_ODD) {
        notificationStore.setNotificationType(betResponseCodeByName.InvalidMinOdd);
        setTimeout(() => notificationStore.setNotificationType(null), 1000);
        doBetStore.setIsBetAccepted(false);
      } else if (+doBetStore.selectedOdd > maxOddValue) {
        notificationStore.setNotificationType(betResponseCodeByName.InvalidMaxOdd);
        setTimeout(() => notificationStore.setNotificationType(null), 1000);
        doBetStore.setIsBetAccepted(false);
      } else {
        gameStore.setIsBetsClosed(true);
        if (autoBetStore.autoBetCount) {
          if (!autoBetStore.isPlay) {
            autoBetStore.setIsPlay();
          }
        }
        // if (applicationStore.isDemo) {
        //   if (userInfoStore.balance >= doBetStore.selectedAmount) {
        //     gameLiveConnection.doDemoBet(doBetStore.selectedAmount, +doBetStore.selectedOdd);
        //   } else {
        //     gameStore.setIsBetsClosed(false);
        //     doBetStore.setIsBetAccepted(false);
        //     if (autoBetStore.isPlay) {
        //       autoBetStore.resetAutoBet();
        //     }
        //     notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance);
        //     setTimeout(() => notificationStore.setNotificationType(null), 1000);
        //   }
        // } else {
        const doBetModel = {
          amount: doBetStore.selectedAmount,
          gameTypeId: CRAZY_WORM_GAME_TYPE_ID,
          betData: {
            odd: +doBetStore.selectedOdd,
            isAutoPlay: !!autoBetStore.autoBetCount,
          },
        };

        if (activeFreeBet.id) {
          doBetModel.freeBetId = activeFreeBet.id;
          doBetModel.amount = activeFreeBet.amount;
        }

        const { doBetDelay } = gameStore;
        const { isAnimationOn } = userSettingsStore;

        if (isAnimationOn && doBetDelay) {
          setTimeout(() => {
            gameLiveConnection.doBet(doBetModel);
          }, doBetDelay * 1000);
        } else {
          gameLiveConnection.doBet(doBetModel);
        }
      }
      // }
    }
  }

  // handleDoDemoBetResult({ amount, winAmount, result }) {
  //   const { isAnimationOn } = userSettingsStore;
  //   if (isAnimationOn) {
  //     webGameSceneDrawer.crazyWormShoot();
  //   }
  //   userInfoStore.setDemoBalance(userInfoStore.balance - amount);
  //   setTimeout(
  //     () => {
  //       doBetStore.setIsBetAccepted(false);
  //       doBetStore.setRoundFinResult(+result);
  //     },
  //     isAnimationOn ? 500 : 0
  //   );
  //   setTimeout(
  //     () => {
  //       doBetStore.setRoundIsFin(true);
  //     },
  //     isAnimationOn ? 900 : 0
  //   );
  //   const liveDemoBetData = {
  //     amount,
  //     result,
  //     winAmount,
  //     odd: doBetStore.selectedOdd,
  //     id: createGuid(),
  //   };
  //   this.handleRoundWinning(winAmount);
  //   setTimeout(
  //     () => {
  //       gameStore.updatedPastRoundsDemoData(liveDemoBetData);
  //     },
  //     isAnimationOn ? 1000 : 0
  //   );
  // }

  handleLiveBetsResult(liveBet) {
    const { isAnimationOn } = userSettingsStore;
    setTimeout(
      () => {
        liveBetsDataStore.updateLiveBets(liveBet);
      },
      isAnimationOn ? 500 : 0
    );
    // setTimeout(() => freeBetStore.handleFreeBetEndedData(), 1500);
  }

  handleRoundWinning(betResult, freeBetId = null) {
    const { isAnimationOn, isSoundOn } = userSettingsStore;
    const { winAmount, balance } = betResult;

    if (balance || balance === 0) {
      userInfoStore.setBalance(balance);
    }

    if (winAmount) {
      setTimeout(() => gameLiveConnection.getBalance(), 2000);

      if (isAnimationOn) {
        setTimeout(() => {
          webGameSceneDrawer.shootSmokeWin();
        }, 800);
      }
      setTimeout(
        () => {
          if (freeBetId) {
            const currentFreeBetMaxPayout = freeBetStore.freeBets.find(
              (item) => item.id === freeBetId
            ).maxPayout;

            if (currentFreeBetMaxPayout && winAmount >= currentFreeBetMaxPayout) {
              notificationStore.setNotificationType(BetResponseCode.FreeBetMaxWinPayout);
              setTimeout(() => notificationStore.setNotificationType(null), 1000);
            }
            const winData = {
              winAmount,
              freeBetId,
            };
            freeBetStore.freeBetTotalWinCalculator(winData);
          } else {
            if (winAmount >= gameConfigStore.limits.maxWin) {
              notificationStore.setNotificationType(BetResponseCode.MaxWinLimitNote);
              setTimeout(() => notificationStore.setNotificationType(null), 1000);
            }
          }
          if (isSoundOn) {
            soundService.playWin();
            soundService.play(soundTypes.crazyWin);
          }
          winningStore.setWinningAmount(winAmount, applicationStore.isDemo);
          winningStore.setIsWin(true);
          setTimeout(
            () => {
              this.handleAutoBet();
              this.handleResetEndedRoundData();
              winningStore.setIsWin(false);
              winningStore.setLastWin(winAmount);
            },
            isAnimationOn ? 800 : 500
          );
          if (!isAnimationOn) {
            gameStore.setIsBetsClosed(false);
          }
        },
        isAnimationOn ? 800 : 0
      );
    } else {
      if (isAnimationOn) {
        setTimeout(() => {
          webGameSceneDrawer.shootSmokeLoose();
        }, 800);
      }
      setTimeout(
        () => {
          this.handleAutoBet();
        },
        isAnimationOn ? 1300 : 500
      );
      if (!isAnimationOn) {
        gameStore.setIsBetsClosed(false);
      }
      setTimeout(
        () => {
          this.handleResetEndedRoundData();
        },
        isAnimationOn ? 800 : 500
      );
      winningStore.clearWinInfo();
    }
    // setTimeout(() => freeBetStore.handleFreeBetEndedData(), 1000);
  }

  handleAutoBet() {
    if (
      (autoBetStore.autoBetCount - 1 >= 1 || autoBetStore.autoBetCount === INFINITY_AUTO_BET) &&
      autoBetStore.isPlay
    ) {
      if (autoBetStore.autoBetCount !== INFINITY_AUTO_BET) {
        autoBetStore.decrementAutoBetCount();
      }
      this.sendBet();
    } else {
      autoBetStore.resetAutoBet();
      if (userSettingsStore.isAnimationOn) {
        gameStore.setIsBetsClosed(false);
      }
    }
  }

  handleResetEndedRoundData() {
    if (!applicationStore.isDemo) {
      tableBetHistoryStore.handleBetHistoryUpdate();
    }
    roundInfoStore.setRoundId(null);
  }

  handleResetDataInitial() {
    winningStore.setLastWin(0);
    winningStore.clearWinInfo();
    // gameStore.setIsBetsClosed(false);
    autoBetStore.resetAutoBet();
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then(() => {
          liveConnection.connectionInitCallback(false);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  handleReconnecting(error) {
    webGameSceneDrawer.clearAllGameTracks();
    if (error && error.message) {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    } else {
      GameService.ReconnectCount += 1;
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    if (popupStore.popupName !== PopUps.ConnectionError) {
      gameLiveConnection.sendGameGetInitialState(false).then(() => {});
    }
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  handleGameOpen() {
    winningStore.setLastWin(0);
    winningStore.clearWinInfo();
    gameStore.setIsBetsClosed(false);
    autoBetStore.resetAutoBet();

    gameLiveConnection.stop();
  }
}

export const gameService = new GameService();
